import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, LogIn, Calendar, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = "Login - Penjadwalan Acara Pimpinan";
  });

  const handleSubmit = async (e) => {
    // KRITIS: preventDefault mencegah reload halaman
    e.preventDefault();
    // Reset error dulu, jangan reload
    setError('');
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate('/');
    } catch (err) {
      // Tampilkan pesan error di UI, TANPA reload
      const msg = err.response?.data?.message || 'Username atau password salah';
      setError(msg);
      // Focus kembali ke field password agar user bisa coba lagi
      setTimeout(() => {
        document.getElementById('login-password')?.focus();
        document.getElementById('login-password')?.select();
      }, 50);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    // Hapus error saat user mulai mengetik lagi
    if (error) setError('');
  };

  return (
    <div className="login-page">
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 64, height: 64, background: 'rgba(255,255,255,0.15)', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <Calendar size={32} color="white" />
          </div>
          <h1 style={{ color: 'white', fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>Penjadwalan Acara Pimpinan</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginTop: 4 }}></p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 4 }}>Sekretariat DPRD Provinsi DKI Jakarta</p>
        </div>

        {/* Card */}
        <div style={{ background: 'white', borderRadius: 20, padding: 36, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 }}>Masuk ke Sistem</h2>
          <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 24 }}>Masukkan kredensial Anda untuk melanjutkan</p>

          {/* Alert error — muncul tanpa reload */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '12px 14px', marginBottom: 20,
              background: 'var(--red-bg)',
              border: '1px solid #FCA5A5',
              borderRadius: 10,
              animation: 'fadeInDown 0.2s ease',
            }}>
              <AlertCircle size={18} color="var(--red)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#B91C1C' }}>Login Gagal</div>
                <div style={{ fontSize: 13, color: '#B91C1C', marginTop: 2 }}>{error}</div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            // noValidate mencegah browser validation popup yang bisa menyebabkan behavior aneh
            noValidate>

            <div className="form-group">
              <label className="form-label" htmlFor="login-username">Username</label>
              <input
                id="login-username"
                className={`form-input ${error ? 'input-error' : ''}`}
                type="text"
                placeholder="Masukkan username"
                value={form.username}
                onChange={handleChange('username')}
                autoComplete="username"
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password"
                  className={`form-input ${error ? 'input-error' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Masukkan password"
                  value={form.password}
                  onChange={handleChange('password')}
                  autoComplete="current-password"
                  disabled={loading}
                  style={{ paddingRight: 44, width: '100%' }}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} tabIndex={-1}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', padding: 0 }}>
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-full"
              disabled={loading || !form.username || !form.password}
              style={{ marginTop: 8 }}
            >
              {loading
                ? <><span className="loader" /> Memproses...</>
                : <><LogIn size={18} /> Masuk</>
              }
            </button>
          </form>

          <div style={{ marginTop: 24, padding: 14, background: 'var(--gray-50)', borderRadius: 10, fontSize: 12, color: 'var(--gray-500)', borderLeft: '3px solid var(--gray-200)' }}>
            <p style={{ fontWeight: 700, marginBottom: 6, color: 'var(--gray-600)' }}>Akun Demo:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p>Admin: <code style={{ fontFamily: 'JetBrains Mono', background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>admin</code> / <code style={{ fontFamily: 'JetBrains Mono', background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>admin123</code></p>
              <p>Staff: <code style={{ fontFamily: 'JetBrains Mono', background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>staff</code> / <code style={{ fontFamily: 'JetBrains Mono', background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>user123</code></p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
